import { useMemo } from 'react'
import type { GroupBase } from 'react-select'
import { observer } from 'mobx-react'

import { Field } from 'Components/Field/Field'
import { FieldTypeEnum } from 'Components/Field/Field.types'
import type { SelectOption } from 'Components/Select/Select.types'

import { useCountryList } from 'Hooks'

import {
  ORGANISATION_CONFIG_LIST_ALLOWED_SHIPPING_COUNTRIES_ATT_NAME,
  ORGANISATION_CONFIG_LIST_ALLOWED_SHIPPING_COUNTRIES_NAME,
} from 'Constants/organisationConfig'

import { useShippingLabelCountryList } from 'Portal/src/hooks/useShippingLabelCountryList/useShippingLabelCountryList'
import { useStore } from 'Portal/src/stores'

import type { CountrySelectProps } from './CountrySelect.types'

export const CountrySelect = observer(
  <
    Option extends SelectOption,
    IsMulti extends boolean,
    Group extends GroupBase<Option> = GroupBase<Option>,
  >(
    props: CountrySelectProps<Option, IsMulti, Group>,
  ) => {
    const { isClearable = true, isShippingLabelList, disabled, topCountryCode, ...rest } = props

    const { organisationStore } = useStore()
    const { organisationConfigList, organisation } = organisationStore

    const customCountyList = organisationConfigList
      ?.find(
        ({ name }) =>
          name === ORGANISATION_CONFIG_LIST_ALLOWED_SHIPPING_COUNTRIES_NAME,
      )
      ?.attributes.find(
        ({ attributeName }) =>
          attributeName ===
          ORGANISATION_CONFIG_LIST_ALLOWED_SHIPPING_COUNTRIES_ATT_NAME,
      )

    const countryList = useCountryList()
    const shippingLabelCountryList = useShippingLabelCountryList()

    const countryOptions = useMemo(() => {
      if (isShippingLabelList) {
        const topCode = (topCountryCode || organisation?.contact?.address?.country || 'DE').toUpperCase()

        const fullList = Object.keys(shippingLabelCountryList).map(isoCode => ({
          label: shippingLabelCountryList[isoCode],
          value: isoCode,
        }))

        const sortedList = fullList.sort((a, b) => a.label.localeCompare(b.label))
        const topOption = sortedList.find(opt => opt.value === topCode) || sortedList.find(opt => opt.value === 'DE')

        if (topOption) {
          return [
            topOption,
            { isDisabled: true, label: '────────────────────', value: 'SEPARATOR' },
            ...sortedList.filter(opt => opt.value !== topOption.value),
          ]
        }

        return sortedList
      }

      const availableCountryIsoCodes = customCountyList?.attributeValue
        ? Object.keys(countryList).filter(isoCode =>
            customCountyList.attributeValue.includes(isoCode),
          )
        : Object.keys(countryList)

      return availableCountryIsoCodes.map(isoCode => ({
        label: countryList[isoCode],
        value: isoCode,
      }))
    }, [
      countryList,
      customCountyList,
      isShippingLabelList,
      shippingLabelCountryList,
      topCountryCode,
      organisation,
    ])

    return (
      <Field
        {...rest}
        selectProps={{
          filterOption: (option, inputValue) => {
            if (!inputValue) {return true}
            if (option.value === 'SEPARATOR') {return false}

            const search = inputValue.toLowerCase()

            return option.label.toLowerCase().split(/\s+/).some(word => word.startsWith(search))
          },
          isClearable,
          isDisabled: disabled,
          options: countryOptions,
        }}
        type={FieldTypeEnum.select}
      />
    )
  },
)
